import { FC, useState } from 'react';
import { useNavigate } from 'react-router';
import { Colors } from '~/themes/colors';

interface IProfilePictureHoverProps {
  id: string;
  profileImage?: string;
  isAvailable?: boolean;
  isFavorite?: boolean;
}

const DEFAULT_SIZE = 68;
const HOVER_SIZE = 78;
const BORDER_WIDTH = 3;
const BORDER_OFFSET = BORDER_WIDTH * 2;

const PURPLE_RING_WIDTH = 3;
const PURPLE_RING_GAP = 3;
const PURPLE_RING_TOTAL = (PURPLE_RING_WIDTH + PURPLE_RING_GAP) * 2;

const SEARCH_ICON_SIZE = 26;
const SEARCH_ICON_BORDER = 3;

const SearchIcon: FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="5.5" cy="5.5" r="4.5" stroke={Colors.white} strokeWidth="2" />
    <line
      x1="9"
      y1="9"
      x2="13"
      y2="13"
      stroke={Colors.white}
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

export const ProfilePictureHover: FC<IProfilePictureHoverProps> = ({
  id,
  profileImage,
  isAvailable = true,
  isFavorite = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();

  const visuallyAvailable = isAvailable || isFavorite;
  const showHover = visuallyAvailable && isHovered;
  const currentSize = showHover ? HOVER_SIZE : DEFAULT_SIZE;

  const hasFavoriteRing = isFavorite;
  const hasNeutralBorder = !visuallyAvailable;

  const imageSize = hasFavoriteRing
    ? currentSize - PURPLE_RING_TOTAL
    : hasNeutralBorder
      ? currentSize - BORDER_OFFSET
      : currentSize;

  const handleMouseEnter = () => {
    if (visuallyAvailable) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleClick = () => {
    if (visuallyAvailable) {
      navigate(`/dashboard/professional/${id}`);
    }
  };

  const defaultShadow =
    '0px 3px 10px 1px rgba(0,0,0,0.15), 0px 1px 2px 0px rgba(0,0,0,0.15)';
  const hoverShadow =
    '0px 1px 2px 0px rgba(0,0,0,0.15), 0px 2px 6px 2px rgba(0,0,0,0.15)';

  return (
    <div
      data-testid="profile-picture-hover"
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        position: 'relative',
        width: HOVER_SIZE,
        height: HOVER_SIZE,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: visuallyAvailable ? 'pointer' : 'default',
        flexShrink: 0,
      }}
    >
      {hasFavoriteRing ? (
        <div
          data-testid="favorite-ring"
          style={{
            width: currentSize,
            height: currentSize,
            borderRadius: '50%',
            border: `${PURPLE_RING_WIDTH}px solid ${Colors.favorites_400}`,
            padding: PURPLE_RING_GAP,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxSizing: 'border-box',
            transition: 'width 0.2s, height 0.2s',
          }}
        >
          <img
            data-testid="profile-image"
            src={profileImage}
            alt="Profile"
            style={{
              width: imageSize,
              height: imageSize,
              borderRadius: '50%',
              objectFit: 'cover',
              boxShadow: showHover ? hoverShadow : defaultShadow,
              transition: 'width 0.2s, height 0.2s',
            }}
          />
        </div>
      ) : (
        <img
          data-testid="profile-image"
          src={profileImage}
          alt="Profile"
          style={{
            width: imageSize,
            height: imageSize,
            borderRadius: '50%',
            objectFit: 'cover',
            boxShadow: showHover ? hoverShadow : defaultShadow,
            transition: 'width 0.2s, height 0.2s',
            ...(hasNeutralBorder && {
              border: `${BORDER_WIDTH}px solid ${Colors.neutral_300}`,
            }),
            ...(!isAvailable && {
              filter: 'grayscale(100%)',
              opacity: 0.5,
            }),
          }}
        />
      )}

      {showHover && (
        <div
          data-testid="hover-overlay"
          style={{
            position: 'absolute',
            width: imageSize,
            height: imageSize,
            borderRadius: '50%',
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <span
            style={{
              color: Colors.white,
              fontSize: 15,
              fontFamily: 'Nunito',
              lineHeight: 1,
              textAlign: 'center',
            }}
          >
            View
          </span>
          <span
            style={{
              color: Colors.white,
              fontSize: 15,
              fontFamily: 'Nunito',
              lineHeight: 1,
              textAlign: 'center',
            }}
          >
            Profile
          </span>
        </div>
      )}

      {visuallyAvailable && (
        <div
          data-testid="search-icon-button"
          style={{
            position: 'absolute',
            bottom: -6,
            right: -6,
            width: SEARCH_ICON_SIZE,
            height: SEARCH_ICON_SIZE,
            borderRadius: '50%',
            backgroundColor: showHover ? Colors.secondary_350 : Colors.secondary_500,
            border: `${SEARCH_ICON_BORDER}px solid ${Colors.neutral_50}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.2s',
          }}
        >
          <SearchIcon />
        </div>
      )}
    </div>
  );
};
